/**
 * Derives option-level display for module test admin report (parity with levelTestTimeline toAnswerLabel).
 */
import type { ModuleTestAnswerWithExercise } from "./common.interfaces";

function parseJsonSafe(value: unknown): unknown {
  if (typeof value !== "string") return value;
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
}

function normalizeAnswerValues(answerData: string): Array<string | number> {
  const parsed = parseJsonSafe(answerData);
  if (Array.isArray(parsed)) {
    return parsed
      .map((v) => (typeof v === "number" || typeof v === "string" ? v : String(v)))
      .filter((v) => String(v).length > 0);
  }
  if (parsed == null || parsed === "") return [];
  if (typeof parsed === "number" || typeof parsed === "string") return [parsed];
  return [String(parsed)];
}

function toBoolean(value: unknown): boolean {
  if (value === true || value === 1) return true;
  if (typeof value === "string") return value.toLowerCase() === "true";
  return false;
}

/**
 * Which option index(es) the stored answer refers to. Prioritise `id` over `order` so a value like `1`
 * does not match both `id: 1` and `order: 1` on two different options.
 */
/** Shared with level test timeline (same id vs order priority). */
export function resolveSelectedOptionIndices(
  optionSource: Array<Record<string, unknown>>,
  selectedValues: Array<string | number>
): Set<number> {
  const out = new Set<number>();
  if (optionSource.length === 0 || selectedValues.length === 0) return out;
  for (const raw of selectedValues) {
    if (raw === "" || raw == null) continue;
    const sNum = Number(raw);
    const sStr = String(raw);
    let idx = -1;
    for (let i = 0; i < optionSource.length; i++) {
      const o = optionSource[i]!;
      if (o.id != null && o.id !== "" && (String(o.id) === sStr || Number(o.id) === sNum)) {
        idx = i;
        break;
      }
    }
    if (idx < 0) {
      for (let i = 0; i < optionSource.length; i++) {
        const o = optionSource[i]!;
        const ord = Number(o.order ?? i + 1);
        if (sNum === ord || sStr === String(ord)) {
          idx = i;
          break;
        }
      }
    }
    if (idx < 0 && Number.isInteger(sNum) && sNum >= 0 && sNum < optionSource.length) {
      const noneHaveOrder = optionSource.every(
        (o) => o == null || !Object.prototype.hasOwnProperty.call(o, "order")
      );
      if (noneHaveOrder) {
        out.add(sNum);
        continue;
      }
    }
    if (idx >= 0) out.add(idx);
  }
  return out;
}

export type ModuleTestAnswerOptionRow = {
  order: number;
  text: string;
  isCorrect: boolean;
  isSelected: boolean;
};

export function buildModuleTestMultipleChoiceDisplay(answer: ModuleTestAnswerWithExercise): {
  exerciseTitle?: string;
  questionText?: string;
  options: ModuleTestAnswerOptionRow[];
} {
  const selectedValues = normalizeAnswerValues(answer.answerData);
  const rawData = answer.exercise?.data;
  const exerciseData = parseJsonSafe(rawData) as Record<string, unknown> & {
    options?: Array<Record<string, unknown>>;
  };
  const optionSource = Array.isArray(exerciseData?.options) ? exerciseData.options : [];
  const selectedIdx = resolveSelectedOptionIndices(optionSource, selectedValues);
  const options: ModuleTestAnswerOptionRow[] = optionSource.map(
    (option: Record<string, unknown>, optIndex: number) => {
      const optionOrder = Number(option?.order ?? optIndex + 1);
      return {
        order: optionOrder,
        text: String(option?.text ?? ""),
        isCorrect: toBoolean(option?.isCorrect),
        isSelected: selectedIdx.has(optIndex),
      };
    }
  );
  const questionText =
    typeof exerciseData?.question === "string" ? exerciseData.question : undefined;
  return {
    exerciseTitle: answer.exercise?.title ?? undefined,
    questionText,
    options,
  };
}
